import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LogoutIcon from '@mui/icons-material/Logout';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { selfCheckIn, selfCheckOut } from '../../services/volunteerApi';
import { VolunteerShiftRegistration } from '../../types/volunteer';
import { checkInState } from '../../utils/volunteerCheckIn';
import { volunteerErrorMessage } from '../../utils/volunteerErrors';

interface Props {
  registration: VolunteerShiftRegistration;
  /** Called after a successful check-in or check-out, with a message to show. */
  onChanged: (message: string) => void;
}

/** The volunteer's own Check in / Check out buttons for one shift. Renders nothing outside the window. */
const ShiftCheckInActions: React.FC<Props> = ({ registration, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOut, setConfirmOut] = useState(false);
  const state = checkInState(registration);
  if (state.kind === 'none') return null;

  const run = async (action: 'in' | 'out') => {
    setBusy(true);
    setError(null);
    try {
      if (action === 'in') {
        await selfCheckIn(registration.shiftId);
        onChanged("You're checked in. Thank you for volunteering!");
      } else {
        await selfCheckOut(registration.shiftId);
        onChanged("You're checked out. Your hours have been recorded.");
      }
    } catch (err) {
      setError(volunteerErrorMessage(err, action === 'in' ? 'Could not check you in' : 'Could not check you out'));
    } finally {
      setBusy(false);
      setConfirmOut(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {state.kind === 'not-yet' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <ScheduleIcon fontSize="small" />
          <Typography variant="body2">Check-in opens at {state.opensAt}</Typography>
        </Box>
      )}
      {state.kind === 'check-in' && (
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          startIcon={<HowToRegIcon />}
          onClick={() => run('in')}
          disabled={busy}
        >
          {busy ? 'Checking in...' : 'Check in'}
        </Button>
      )}
      {state.kind === 'check-out' && (
        <Button
          variant="outlined"
          fullWidth
          size="large"
          startIcon={<LogoutIcon />}
          onClick={() => setConfirmOut(true)}
          disabled={busy}
        >
          Check out
        </Button>
      )}

      <Dialog open={confirmOut} onClose={() => !busy && setConfirmOut(false)}>
        <DialogTitle>Check out now?</DialogTitle>
        <DialogContent>
          <Typography>
            Only check out when you have finished your shift. Your hours will be recorded from your
            check-in time until now.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOut(false)} disabled={busy}>
            Not yet
          </Button>
          <Button variant="contained" onClick={() => run('out')} disabled={busy}>
            {busy ? 'Checking out...' : 'Check out'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftCheckInActions;
